import React from "react";
import { Coins, Crown, Info, TrendingUp, Wallet } from "lucide-react";
import { Btn, Callout, Card, CardBody, CardHeader, KPI, KV } from "../components/primitives";

const OverviewTab = ({ config, goTo }) => {
  if (!config) return <div className="p-6 text-[13px] text-[#64748B]">Loading…</div>;

  const earn = config.earn_config || {};
  const redeem = config.redeem_config || {};
  // Tier roster lives under earn_config post senior-review reshape; the
  // per-tier redeem cap lives under redeem_config. Join by id for the
  // overview cards (Tier name + multiplier from earn, cap from redeem).
  const earnTiers = earn.tiers || [];
  const redeemTiers = redeem.tiers || [];
  const capById = new Map(redeemTiers.map((r) => [r.id, r.redeem_cap_pct]));
  const tiers = earnTiers.map((t) => ({
    ...t,
    redeem_cap_pct: capById.has(t.id) ? capById.get(t.id) : 0,
  }));

  const exampleBill = 1000;
  const examplePts = Math.floor((exampleBill / (earn.per || 100)) * (earn.rate || 1));
  const exampleValue = examplePts * (redeem.pt_value || 0);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-[17px] font-bold text-[#0F172A]">Loyalty Overview</h2>
          <p className="text-[13px] text-[#64748B] mt-1 max-w-[680px]">
            Configure how customers earn and redeem points. Coupons, series and schemes will arrive in a follow-up release.
          </p>
        </div>
        <div className="flex gap-2">
          <Btn variant="ghost" onClick={() => goTo("earn")}>
            <Coins size={15} />
            Earning rules
          </Btn>
          <Btn variant="primary" onClick={() => goTo("redeem")}>
            <Wallet size={15} />
            Redemption
          </Btn>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI
          icon={<Coins size={18} />}
          tone="gold"
          label="Base earn rate"
          value={`${earn.rate || 0} pt / ₹${earn.per || 0}`}
          delta={`Rounded ${earn.rounding || "down"}`}
        />
        <KPI
          icon={<Wallet size={18} />}
          tone="green"
          label="Point value"
          value={`₹${(redeem.pt_value || 0).toFixed(2)}`}
          delta={`Min ${redeem.min_redeem || 0} pts to redeem`}
        />
        <KPI
          icon={<TrendingUp size={18} />}
          tone="blue"
          label="Max redemption / bill"
          value={`${redeem.max_pct_of_bill || 0}%`}
          delta={`Min bill ₹${redeem.min_bill_to_redeem || 0}`}
        />
        <KPI
          icon={<Crown size={18} />}
          tone="purple"
          label="Membership tiers"
          value={tiers.length}
          delta="Editable"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-4 items-start">
        <Card>
          <CardHeader
            icon={<Crown size={18} />}
            tone="gold"
            title="Membership tiers"
            action={
              <Btn size="sm" variant="ghost" onClick={() => goTo("tiers")}>
                Edit
              </Btn>
            }
          />
          <CardBody tight>
            {tiers.length === 0 ? (
              <div className="text-[13px] text-[#64748B] py-3">No tiers configured.</div>
            ) : (
              tiers.map((t) => (
                <KV
                  key={t.id}
                  label={
                    <span className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm" style={{ background: t.color }} />
                      <span className="font-semibold text-[#0F172A]">{t.name}</span>
                    </span>
                  }
                  value={
                    <span className="tabular-nums">
                      {t.multiplier}× · {t.redeem_cap_pct}% cap
                    </span>
                  }
                />
              ))
            )}
          </CardBody>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader
              icon={<Coins size={18} />}
              tone="green"
              title="Live earn example"
              desc={`On a ₹${exampleBill.toLocaleString("en-IN")} bill, a member earns…`}
            />
            <CardBody tight>
              <div className="text-[32px] font-bold text-[#0F172A] tracking-tight leading-none">
                {examplePts} pts
              </div>
              <div className="text-[12px] text-[#64748B] mt-1">
                ≈ ₹{exampleValue.toFixed(2)} in future redemption value
              </div>
              <div className="pt-3 mt-2 border-t border-[#F1F4F8]">
                <KV label="Earn rate" value={`${earn.rate || 0} pt / ₹${earn.per || 0}`} />
                <KV label="Expiry" value={earn.expiry_months ? `${earn.expiry_months} mo` : "Never"} />
                <KV label="Signup bonus" value={`${earn.signup_bonus || 0} pts`} />
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      <Callout tone="blue" icon={<Info size={16} />}>
        Changes here apply to <b>new transactions</b> only. Bills already settled are not recalculated.
      </Callout>
    </div>
  );
};

export default OverviewTab;
